import { useEffect, useMemo, useState } from "react";
import type { AxiosProgressEvent } from "axios";
import axios from "axios";

import type { ID } from "@/constants";
import type { IFileMetaData } from "@/stores/fileStore";
import { filesStorFn, useFileStore } from "@/stores/fileStore";
import { generateUUIDv4 } from "@/utils";
import { uploadFileAPI } from "@/api/file";
import type { IUploadFileResponseType } from "@/api/file/files";

export interface IUploadFileVariable {
  file: File;
  fileID: ID;
}

interface IUseFileHandlerOptions {
  groupFilesID?: ID;
}

export interface IUploadMutationOptions {
  onSuccess?: (
    data: IUploadFileResponseType,
    variables: IUploadFileVariable,
  ) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (error: any, variables: IUploadFileVariable) => void;
}

/**
 * Use this when you want to use only the functionality of file handing.
 *
 * ATTENTION: This will NOT rerender the component when any files modify
 *
 * @param options
 *
 * @returns
 */
export function useFileHandlerFns(options?: IUseFileHandlerOptions) {
  const [localFileID] = useState(options?.groupFilesID || generateUUIDv4());

  // rerendering when files isLoading flag has changed
  useFileStore((state) => state.isLoadingModifySignal);

  useEffect(() => {
    const files = filesStorFn.files;
    const groupFilesIsLoading = filesStorFn.groupFilesIsLoading;
    // initial new set of filesMetadata

    if (!files.has(localFileID)) {
      files.set(localFileID, new Map());
      groupFilesIsLoading.set(localFileID, false);
    }

    // cancel every file uploading in component unmounting
    return () => {
      if (options?.groupFilesID !== undefined) return;

      functionalities.cancelAll();
      // and delete from the set of the groupOfFilesMetadata
      files.delete(localFileID);
      filesStorFn.updateFilesSignal();

      groupFilesIsLoading.delete(localFileID);
      filesStorFn.updateIsLoadingSignal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const functionalities = useMemo(() => {
    /**
     * Forces a re-render of the components when files modify.
     */
    function forceUpdate() {
      filesStorFn.updateFilesSignal();
    }

    /**
     * Forces a re-render of the components when isLoading state modify.
     */
    function forceUpdateIsLoading() {
      filesStorFn.updateIsLoadingSignal();
    }

    /**
     * @returns Map<ID, FileMetaData> or undefined
     */
    function _getFilesData(): Map<ID, IFileMetaData> | undefined {
      return filesStorFn.files.get(localFileID);
    }

    /**
     *
     * @returns Map<ID, boolean> or undefined
     */
    function _getFilesIsLoading() {
      return filesStorFn.groupFilesIsLoading.get(localFileID);
    }

    /**
     * @param fn - function to iterate on values
     */
    function _iterateOnValues(fn: (value: IFileMetaData) => void) {
      const values = _getFilesData()?.values();

      if (values) {
        for (const value of values) {
          fn(value);
        }
      }
    }

    /**
     * @param fn - function to iterate on keys
     */
    function _iterateOnKeys(fn: (key: ID) => void) {
      const keys = _getFilesData()?.keys();

      if (keys) {
        for (const key of keys) {
          fn(key);
        }
      }
    }

    /**
     * @param file - File object
     * @return  ID of the file
     */
    function add(file: File): ID {
      const uuid = generateUUIDv4();

      _getFilesData()?.set(uuid, {
        file,
        percent: 0,
        status: "idle",
        source: axios.CancelToken.source(),
      });
      forceUpdate();
      return uuid;
    }

    /**
     * @param id - ID of the file
     * @param fn - function to update file metadata
     * @param rerender - boolean flag to force rerender
     */
    function update(
      id: ID,
      fn: (prevValue: IFileMetaData) => IFileMetaData,
      rerender = true,
    ) {
      const data = _getFilesData()?.get(id);

      if (data) {
        _getFilesData()?.set(id, fn(data));
        if (rerender) forceUpdate();
      }
    }

    function updateIsLoading(value: boolean) {
      if (_getFilesIsLoading() !== value) {
        if (value === true) {
          filesStorFn.groupFilesIsLoading.set(localFileID, true);
          forceUpdateIsLoading();
          return;
        }
        // otherwise
        if (getAllUploadingFiles().length === 0) {
          filesStorFn.groupFilesIsLoading.set(localFileID, false);
          forceUpdateIsLoading();
        }
      }
    }

    /**
     * @param id - ID of the file to delete
     */
    function deleteFile(id: ID) {
      _getFilesData()?.get(id)?.source.cancel();
      _getFilesData()?.delete(id);
      forceUpdate();
    }

    /**
     * @param id - ID of the file to cancel
     */
    function cancel(id: ID) {
      // cancel file upload
      _getFilesData()?.get(id)?.source.cancel();

      // update file status and refreshing cancel token
      update(id, (prev) => ({
        ...prev,
        status: "error",
        source: axios.CancelToken.source(),
      }));
    }

    /**
     * @param fileID - ID of the file being uploaded
     * @param progressEvent - AxiosProgressEvent object
     */
    function onProgressCallback(fileID: ID, progressEvent: AxiosProgressEvent) {
      const { progress } = progressEvent;

      if (progress) {
        // calculate the uploaded percentages
        const percent = Math.round(progress * 100);

        // update the percent and rerender
        update(fileID, (prev) => ({ ...prev, percent }), false);
        filesStorFn.updateProgressSignal();
      }
    }

    /**
     * @param id - ID of the file to check for upload eligibility
     * @returns boolean
     */
    function _canUpload(id: ID) {
      const status = _getFilesData()?.get(id)?.status;

      return status === "idle" || status === "error";
    }

    /**
     * @param id - ID of the file to upload
     */
    const uploadFile = (id: ID, options?: IUploadMutationOptions) =>
      new Promise<{
        data: IUploadFileResponseType;
        variables: IUploadFileVariable;
      }>((resolve, reject) => {
        const file = _getFilesData()?.get(id)?.file;

        if (!_canUpload(id) || !file) {
          // TODO: check eslint
          // eslint-disable-next-line prefer-promise-reject-errors
          reject(
            "The file is not able to be uploaded. Its status is not 'idle' or 'error'.",
          );
          return;
        }

        const data = new FormData();

        data.append("file", file);
        data.append("name", file.name);

        const token = _getFilesData()?.get(id)?.source.token;
        const variables = { file, fileID: id };

        update(id, (prev) => ({ ...prev, status: "uploading", percent: 0 }));
        updateIsLoading(true);
        uploadFileAPI(
          data,
          (progressEvent) => onProgressCallback(id, progressEvent),
          token,
        )
          .then((data) => {
            update(id, (prev) => ({
              ...prev,
              status: "success",
              UUID: data.data.data,
            }));
            updateIsLoading(false);
            options?.onSuccess?.(data, variables);
            resolve({ data, variables });
          })
          .catch((error) => {
            update(id, (prev) => ({ ...prev, status: "error" }));
            updateIsLoading(false);
            options?.onError?.(error, variables);
            reject(
              new Error(`something went wrong in file uploading: \n ${error}`),
            );
          });
      });

    /**
     * Upload all files that are eligible for upload
     */
    function uploadAll(options?: IUploadMutationOptions) {
      _iterateOnKeys((key) => uploadFile(key, options));
    }

    /**
     * Cancel upload for all files
     */
    function cancelAll() {
      _iterateOnKeys((key) => cancel(key));
    }

    /**
     *
     * @returns Array of files with status 'idle', 'uploading', or 'error'
     */
    function getAllUnUploadedFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata].filter((item) => item[1].status !== "success");
      }
      return [];
    }

    /**
     *
     * @returns Array of files with status 'uploading'
     */
    function getAllUploadingFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata].filter((item) => item[1].status === "uploading");
      }
      return [];
    }

    /**
     *
     * @returns Array of files with status 'success'
     */
    function getAllUploadedFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata].filter((item) => item[1].status === "success");
      }
      return [];
    }

    /**
     *
     * @returns Array of files with status 'idle'
     */
    function getAllIdleFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata].filter((item) => item[1].status === "idle");
      }
      return [];
    }

    /**
     *
     * @returns Array of files with status 'error'
     */
    function getAllCanceledFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata].filter((item) => item[1].status === "error");
      }
      return [];
    }

    /**
     *
     * @returns Array of files
     */
    function getAllFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata];
      }
      return [];
    }

    /**
     * cancel and clear all files
     */
    function clear() {
      cancelAll();
      _getFilesData()?.clear();
      forceUpdate();
    }

    return {
      add,
      cancel,
      cancelAll,
      clear,
      deleteFile,
      forceUpdate,
      getAllCanceledFiles,
      getAllFiles,
      getAllIdleFiles,
      getAllUnUploadedFiles,
      getAllUploadedFiles,
      getAllUploadingFiles,
      uploadAll,
      uploadFile,
    };
  }, [localFileID]);

  return {
    localFileID,
    isLoading: filesStorFn.groupFilesIsLoading.get(localFileID) || false,
    ...functionalities,
  };
}

/**
 * Use this when you want to use only the functionality of file handing.And rerender the component when files has modified.
 * @param options
 * @returns
 */
export function useFileHandler(options?: IUseFileHandlerOptions) {
  // rerendering when files modify
  useFileStore((state) => state.filesModifySignal);

  return useFileHandlerFns(options);
}
